/**
 * Groupes de workflows N8N dans l'admin — chantier Booster, lot T1
 * (plan validé : `docs/plans/booster-t1-groupes-workflows-n8n.md`).
 *
 * - `WorkflowGroupSelect` : « Groupe de workflows » d'un simulateur (fiche et éditeur). Le choix vide
 *   = le groupe par défaut, affiché avec SON NOM LU EN BASE (jamais un libellé écrit en dur).
 * - `WorkflowGroupIdentityFields` : « Nom » et « Groupe par défaut » d'une ligne de workflows.
 * - `DefaultGroupChangeDialog` : confirmation à deux boutons avant de changer de groupe par défaut,
 *   avec le nombre de simulateurs qui basculeront.
 *
 * Le groupe d'un simulateur et le statut « par défaut » sont réservés à l'ADM (`champsReservesAdm`,
 * `src/lib/n8n-groupes.ts`) : pour les autres profils, `locked` les affiche en lecture.
 */
import { useTranslation } from "react-i18next";
import { AlertTriangle } from "lucide-react";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";

type Row = Record<string, unknown>;

/** Valeur du choix « groupe par défaut » (Radix refuse une valeur vide). */
const PAR_DEFAUT = "__default__";

export function groupeParDefaut(groupes: Row[]): Row | undefined {
  return groupes.find((g) => g.isDefault === true);
}

function useNomDeGroupe() {
  const { t } = useTranslation();
  return (g: Row) => (typeof g.name === "string" && g.name ? g.name : t("admin.n8nGroup.unnamed", { id: String(g.id ?? "").slice(0, 8) }));
}

export function WorkflowGroupSelect({
  value,
  onChange,
  groupes,
  locked = false,
  compact = false,
}: {
  value: unknown;
  onChange: (id: string | null) => void;
  groupes: Row[];
  locked?: boolean;
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const nomDe = useNomDeGroupe();
  const defaut = groupeParDefaut(groupes);
  const libelleDefaut = defaut && typeof defaut.name === "string" && defaut.name
    ? t("admin.n8nGroup.defaultOption", { name: defaut.name })
    : t("admin.n8nGroup.defaultOptionNoName");
  const autres = groupes.filter((g) => g.isDefault !== true);
  const courant = typeof value === "string" && value ? value : PAR_DEFAUT;

  return (
    <div className="space-y-1" data-testid="simulator-workflow-group">
      <Label className="text-xs text-slate-500">{t("admin.n8nGroup.label")}</Label>
      <Select value={courant} onValueChange={(v) => onChange(v === PAR_DEFAUT ? null : v)} disabled={locked}>
        <SelectTrigger className={`w-full ${compact ? "text-sm" : ""}`}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value={PAR_DEFAUT}>{libelleDefaut}</SelectItem>
          {autres.map((g) => (
            <SelectItem key={String(g.id)} value={String(g.id)}>{nomDe(g)}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      {locked && <p className="text-[11px] text-slate-500">{t("admin.n8nGroup.adminOnly")}</p>}
    </div>
  );
}

export function WorkflowGroupIdentityFields({
  formData,
  onChange,
  etaitParDefaut,
  defaultLocked,
}: {
  formData: Row;
  onChange: (key: string, value: unknown) => void;
  /** La ligne était le groupe par défaut à l'ouverture : la marque ne se retire pas, elle se déplace. */
  etaitParDefaut: boolean;
  /** Profil sans droit sur le statut « par défaut » (réservé à l'ADM). */
  defaultLocked: boolean;
}) {
  const { t } = useTranslation();
  const coche = formData.isDefault === true;
  const desactive = defaultLocked || etaitParDefaut;
  return (
    <div className="grid grid-cols-2 gap-3">
      <div className="space-y-1">
        <Label className="text-xs text-slate-500">{t("admin.n8nGroup.name")}</Label>
        <Input value={typeof formData.name === "string" ? formData.name : ""} onChange={(e) => onChange("name", e.target.value)} />
      </div>
      <div className="space-y-1">
        <Label className="text-xs text-slate-500">{t("admin.n8nGroup.isDefault")}</Label>
        <label className={`flex items-center gap-2 h-9 text-sm ${desactive ? "text-slate-400" : "text-slate-700"}`}>
          <input
            type="checkbox"
            checked={coche}
            disabled={desactive}
            onChange={(e) => onChange("isDefault", e.target.checked)}
            data-testid="n8n-group-is-default"
          />
          {coche ? t("admin.n8nGroup.yes") : t("admin.n8nGroup.no")}
        </label>
        {defaultLocked
          ? <p className="text-[11px] text-slate-500">{t("admin.n8nGroup.adminOnly")}</p>
          : etaitParDefaut && <p className="text-[11px] text-slate-500">{t("admin.n8nGroup.isDefaultLocked")}</p>}
      </div>
    </div>
  );
}

export function DefaultGroupChangeDialog({
  open,
  nomDuGroupe,
  simulateursConcernes,
  onConfirm,
  onCancel,
  loading = false,
}: {
  open: boolean;
  nomDuGroupe: string;
  simulateursConcernes: number;
  onConfirm: () => void;
  onCancel: () => void;
  loading?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onCancel(); }}>
      <DialogContent className="sm:max-w-[460px]" data-testid="n8n-default-change-dialog">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
            </div>
            <DialogTitle>{t("admin.n8nGroup.confirmTitle")}</DialogTitle>
          </div>
          <DialogDescription className="pt-2">
            {t("admin.n8nGroup.confirmBody", { count: simulateursConcernes, name: nomDuGroupe })}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="ghost" onClick={onCancel} disabled={loading}>{t("common.cancel")}</Button>
          <Button onClick={onConfirm} disabled={loading} className="bg-[#384872] hover:bg-[#2d3a5c] text-white">
            {loading ? t("common.loading") : t("admin.n8nGroup.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
